import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { ChevronDown, ChevronUp, X } from "lucide-react";

import StaffChatBox from "react-lib/apps/MSG/StaffChatBox";
import BookingSection from "react-lib/apps/MSG/BookingSection";
import type { PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";

import { AlertV4, ButtonV4, ChipV4, ModalV4, SelectV4 } from "../common/ui";
import {
  APPOINTMENT_LIST_CARD,
  CHAT_TABS,
  PENDING_TEXT,
  appointmentViews,
  chatInitial,
  chatListDate,
  filterChatRooms,
  latestMessageText,
  patientDetailEvents,
  patientDisplayName,
  patientInfoRows,
  roomName,
  tabShowsCards,
  unreadBadge,
  type ChatAppointment,
  type ChatPatient,
  type ChatProxyPatient,
  type ChatRoom,
  type ChatTabKey,
} from "./chatModelV4";

import "./_hisv4-chat.scss";

type ChatEvent = { message: string; params?: Record<string, any> };

export type CardChatV4Props = PickMainState<"errorMessage"> & {
  onEvent: (event: ChatEvent) => any;
  setProp: (key: string, value: any, callback?: any) => any;
  aiBotLists?: { divisionId: number; aiLists: { name: string }[] };
  apiToken: string;
  chatController: any;
  chatRoomList?: ChatRoom[];
  createAppointment?: any;
  createdAppointment?: any;
  divisionId?: number | string | null;
  hideCallButton?: boolean;
  loadingStatus?: Record<string, any>;
  modOpenLinkEncounter?: string;
  officialAccountList?: any;
  openPatientChatId?: number | null;
  selectedPatientAppointmentList?: ChatAppointment[];
  selectedPatientChat?: ChatPatient | null;
  selectedProxyPatient?: ChatProxyPatient | null;
  timeDivision?: any[];
  userId: string;
  onCloseModLinkEncounter?: () => void;
  onGoAppointments?: () => void;
  onOpenVideoCall?: (url: string, chatChannelId: string) => void;
  onReadAllMessages?: () => void;
};

/** สิ่งที่ MainHIS อ่านผ่าน ref (shape เดียวกับ V3 StaffChat) */
export type CardChatV4Handle = {
  chatBoxRef: any;
  getChatChannelList: (params?: Record<string, any>) => void;
};

const HUMAN_BOT = "human";

const Avatar = (props: { name: string; size: "sm" | "md" | "lg" }) => (
  <span className={`hv4-chat-avatar hv4-chat-avatar--${props.size}`} aria-hidden="true">
    {chatInitial(props.name)}
  </span>
);

const CardChatV4 = React.forwardRef<CardChatV4Handle, CardChatV4Props>((props, ref) => {
  const [query, setQuery] = useState("");
  const [isUnread, setIsUnread] = useState(false);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatRoom | null>(null);
  const [tab, setTab] = useState<ChatTabKey>("profile");
  const [openInfo, setOpenInfo] = useState(true);
  const [openAppt, setOpenAppt] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookCollapsed, setBookCollapsed] = useState(false);
  const [bookDone, setBookDone] = useState("");
  const [aiBot, setAiBot] = useState("");
  // chatRoomList ตอนส่งคำขอ: ยังเป็นตัวเดิม = รอผล
  const [waitingList, setWaitingList] = useState<{ list: unknown }>({ list: props.chatRoomList });

  const chatBoxRef = useRef<any>(null);
  const bookRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const isFinish = useRef(false);
  const isSearch = useRef(false);
  const channelIdRef = useRef<number | null>(null);
  const openPatientIdRef = useRef<number | null>(null);
  const lastCreatedRef = useRef<any>(props.createdAppointment);

  const name = roomName(selectedChat);
  // รอผลคำขอ หรือได้รายการแล้วแต่ rooms ยังไม่อัปเดต
  const roomsLoading =
    rooms.length === 0 &&
    (waitingList.list === props.chatRoomList ||
      !!props.chatRoomList?.some((item) => !!item.division));

  // Memo
  const visibleRooms = useMemo(() => filterChatRooms(rooms, query), [rooms, query]);

  const appointments = useMemo(
    () => appointmentViews(props.selectedPatientAppointmentList),
    [props.selectedPatientAppointmentList],
  );

  const botOptions = useMemo(
    () => [
      { value: HUMAN_BOT, text: "normal chat" },
      ...(props.aiBotLists?.aiLists ?? []).map((item) => ({ value: item.name, text: item.name })),
    ],
    [props.aiBotLists],
  );

  // Effect
  useImperativeHandle(ref, () => ({
    chatBoxRef: chatBoxRef.current,
    getChatChannelList: (params?: Record<string, any>) => {
      isFinish.current = true;
      handleGetChatChannelList(params);
    },
  }));

  // เปิดห้องจาก ?channel_id= ของ URL
  useEffect(() => {
    const channelId = new URLSearchParams(globalThis.location.search).get("channel_id");
    if (!channelId) {
      return;
    }
    channelIdRef.current = Number(channelId);
    const url = new URL(globalThis.location.origin);
    url.searchParams.set("app", "HIS");
    globalThis.history.replaceState({}, "", url);
  }, []);

  useEffect(() => {
    setSelectedChat(null);
    handleGetChatChannelList();
    props.onEvent({ message: "GetUsableAIList", params: { divisionId: props.divisionId } });
  }, [props.divisionId]);

  // เปิดห้องของผู้รับบริการที่ส่งมาจากหน้าอื่น
  useEffect(() => {
    if (!props.openPatientChatId) {
      return;
    }
    openPatientIdRef.current = props.openPatientChatId;
    props.setProp("openPatientChatId", null);
    handleGetChatChannelList();
  }, [props.openPatientChatId]);

  useEffect(() => {
    const list = (props.chatRoomList ?? [])
      .filter((item) => !!item.division)
      .map((item) =>
        selectedChat && item.id === selectedChat.id ? { ...item, unread_message_count: 0 } : item,
      );
    setRooms(list);

    if (channelIdRef.current && list.length > 0) {
      const found = list.find((item) => item.chat_channel_id === channelIdRef.current);
      channelIdRef.current = null;
      if (found) {
        handleSelectChat(found);
        return;
      }
    }

    if (openPatientIdRef.current && props.chatRoomList) {
      const found = list.find((item) => item.patient === openPatientIdRef.current);
      openPatientIdRef.current = null;
      if (found) {
        handleSelectChat(found);
      } else {
        props.setProp("errorMessage.StaffChat", "ไม่พบห้องแชทของผู้รับบริการรายนี้");
      }
      return;
    }

    if (!selectedChat) {
      return;
    }
    const current = list.find((item) => item.id === selectedChat.id);
    if (!current) {
      setSelectedChat(null);
    } else if (isFinish.current) {
      isFinish.current = false;
      setSelectedChat(current);
    }
  }, [props.chatRoomList]);

  useEffect(() => {
    const sum = rooms.reduce((total, item) => total + (Number(item.unread_message_count) || 0), 0);
    if (!sum && rooms.length > 0) {
      props.onReadAllMessages?.();
    }
  }, [rooms]);

  useEffect(() => {
    if (!selectedChat) {
      return;
    }
    for (const event of patientDetailEvents(selectedChat)) {
      props.onEvent(event);
    }
  }, [selectedChat?.id]);

  // เปิดฟอร์มเพิ่มนัดของ BookingSection (ปุ่มแรกบนหัว section) ทันทีที่เปิดกล่องสร้างนัด
  useEffect(() => {
    if (!bookOpen) {
      return;
    }
    bookRef.current?.querySelector<HTMLButtonElement>("button")?.click();
    asideRef.current?.scrollTo?.({ top: 0 });
  }, [bookOpen]);

  useEffect(() => {
    const created = props.createdAppointment;
    if (!created || created === lastCreatedRef.current) {
      return;
    }
    lastCreatedRef.current = created;
    if (bookOpen) {
      setBookDone(
        `สร้างนัดหมาย${created.id ? ` #${created.id}` : ""} แล้ว · ส่งสรุปนัดให้ลูกค้าในแชทแล้ว`,
      );
    }
  }, [props.createdAppointment]);

  // Handler
  const handleGetChatChannelList = (params: Record<string, any> = {}) => {
    setWaitingList({ list: props.chatRoomList });

    props.onEvent({
      message: "GetChatChannelList",
      params: {
        channelId: "all",
        user: props.userId,
        ...(isSearch.current && { search: query }),
        ...(isUnread && { unread_only: true }),
        ...params,
        chatRoomId: selectedChat?.id,
        isChatPatientOnly: true,
      },
    });
  };

  const handleSelectChat = (room: ChatRoom) => {
    props.setProp("selectedPatientAppointmentList", []);

    if (room.patient) {
      props.setProp(`loadingStatus.${APPOINTMENT_LIST_CARD}`, true);
    }

    if (room.unread_message_count) {
      globalThis.opener?.postMessage(
        {
          event: "message_read",
          data_message_type: "MESSAGE_READ",
          message_id: room.latest_message,
          channel_id: room.chat_channel_id,
        },
        globalThis.location.origin,
      );
    }

    setBookOpen(false);
    setBookDone("");
    setAiBot("");
    setSelectedChat(room);
  };

  const handleSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    isSearch.current = true;
    isFinish.current = true;
    handleGetChatChannelList({ search: query });
  };

  const handleFilter = (unreadOnly: boolean) => {
    if (unreadOnly === isUnread) {
      return;
    }
    setIsUnread(unreadOnly);
    handleGetChatChannelList({ unread_only: unreadOnly });
  };

  const handleReadMessage = () => {
    setRooms((list) =>
      list.map((item) =>
        item.chat_channel_id === selectedChat?.chat_channel_id
          ? { ...item, unread_message_count: 0 }
          : item,
      ),
    );
  };

  const handleClickLink = (e: MouseEvent) => {
    const href = (e.target as HTMLAnchorElement).href || "";
    const params = new URLSearchParams(href.match(/(?!=\/)\?.*$/g)?.[0] || "");
    const encounterId = params.get("encounterId");

    if (encounterId && params.get("app") === "MainDPO") {
      if (e.type === "mousedown") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      props.onEvent({
        message: "HandleOpenLinkEncounter",
        params: { type: e.type, event: e, encounterId, href },
      });
    } else if (e?.button === 0 && href) {
      globalThis.open(href, "_blank", "noopener noreferrer")?.focus();
    }
  };

  const handleChannelDetail = () => {
    if (aiBot) {
      return;
    }
    const detail = chatBoxRef.current?.getChannelDetail?.();
    if (detail?.current_chatbot) {
      setAiBot(detail.current_chatbot);
    } else if (detail?.prefer_chatbot && detail?.is_ai_enable && detail?.default_chatbot?.name) {
      setAiBot(detail.default_chatbot.name);
    }
  };

  const handleChangeBot = async (value: string) => {
    if (!selectedChat?.chat_channel_id) {
      return;
    }
    const result = await props.chatController?.postChatChannelMessage?.({
      content: value,
      contentType: "ai_selection",
      chatChannelId: selectedChat.chat_channel_id,
      apiToken: props.apiToken,
      divisionId: props.divisionId,
    });
    if (result) {
      setAiBot(value);
    }
  };

  const handleChangeQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    isSearch.current = false;
    setQuery(e.target.value);
  };

  const handleCloseError = () => {
    props.setProp("errorMessage.StaffChat", null);
  };

  const handleCloseBooking = () => {
    setBookOpen(false);
  };

  const handleToggleBooking = () => {
    setBookCollapsed((value) => !value);
  };

  const handleToggleInfo = () => {
    setOpenInfo((value) => !value);
  };

  const handleToggleAppt = () => {
    setOpenAppt((value) => !value);
  };

  const handleCloseLinkEncounter = () => {
    props.onCloseModLinkEncounter?.();
  };

  const handleOpenBooking = () => {
    setTab("profile");
    setOpenAppt(true);
    setBookDone("");
    setBookCollapsed(false);
    setBookOpen(true);
  };

  // Render
  return (
    <div className="hv4-chat">
      {/* รายการแชท */}
      <section className="hv4-chat-list" aria-label="รายการแชท">
        <div className="hv4-chat-list-top">
          <input
            className="hv4-input"
            type="search"
            placeholder="ค้นหาชื่อ..."
            aria-label="ค้นหาแชท"
            value={query}
            onChange={handleChangeQuery}
            onKeyDown={handleSearchKey}
          />
          <div className="hv4-chat-filter">
            <ButtonV4
              className={!isUnread ? "hv4-chat-filter-on" : "hv4-chat-filter-off"}
              aria-pressed={!isUnread}
              onClick={() => handleFilter(false)}
            >
              ทั้งหมด
            </ButtonV4>
            <ButtonV4
              className={isUnread ? "hv4-chat-filter-on" : "hv4-chat-filter-off"}
              aria-pressed={isUnread}
              onClick={() => handleFilter(true)}
            >
              ยังไม่ได้อ่าน
            </ButtonV4>
          </div>
        </div>

        <div className="hv4-chat-rooms">
          {visibleRooms.length === 0 && (
            <div className="hv4-chat-rooms-empty">{roomsLoading ? "กำลังโหลด" : "ไม่พบแชท"}</div>
          )}
          {visibleRooms.map((room) => {
            const badge = unreadBadge(room.unread_message_count);
            const active = !!selectedChat && room.id === selectedChat.id;
            return (
              <button
                key={room.id ?? room.chat_channel_id}
                type="button"
                className={`hv4-chat-room${active ? " hv4-chat-room--active" : ""}`}
                aria-current={active ? "true" : undefined}
                onClick={() => handleSelectChat(room)}
              >
                <Avatar name={roomName(room)} size="md" />
                <span className="hv4-chat-room-body">
                  <span className="hv4-chat-room-line">
                    <b className="hv4-chat-room-name">{roomName(room)}</b>
                    <span className="hv4-chat-room-date">
                      {chatListDate(room.latest_message_datetime)}
                    </span>
                  </span>
                  <span className="hv4-chat-room-line">
                    <span className="hv4-chat-room-last">
                      {latestMessageText(room, props.userId)}
                    </span>
                    {badge && <span className="hv4-chat-badge">{badge}</span>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ข้อความ */}
      <section className="hv4-chat-main" aria-label="ข้อความ">
        <AlertV4 tone="danger" error={props.errorMessage?.StaffChat} onClose={handleCloseError} />
        {selectedChat ? (
          <>
            <header className="hv4-chat-head">
              <Avatar name={name} size="sm" />
              <b className="hv4-chat-head-name">{name}</b>
              <label className="hv4-label hv4-chat-head-label" htmlFor="hv4-chat-bot">
                แชทบอท
              </label>
              <SelectV4
                id="hv4-chat-bot"
                className="hv4-chat-head-select"
                aria-label="แชทบอท"
                options={botOptions}
                value={aiBot}
                onChange={handleChangeBot}
              />
            </header>
            <div className="hv4-chat-thread">
              <StaffChatBox
                key={selectedChat.chat_channel_id}
                ref={chatBoxRef}
                onEvent={props.onEvent}
                chatController={props.chatController}
                apiToken={props.apiToken}
                chatBoxStyle={{ height: "100%" }}
                divisionId={String(props.divisionId ?? "")}
                hideCallButton={props.hideCallButton}
                isChatPatientOnly
                menuSameLevelTextArea
                selectedChat={selectedChat}
                userId={props.userId}
                onClickLink={handleClickLink}
                onDidMount={handleReadMessage}
                onOpenVideoCall={props.onOpenVideoCall}
                onSetChannelName={handleChannelDetail}
              />
            </div>
          </>
        ) : (
          <div className="hv4-chat-none">เลือกห้องแชทจากรายการทางซ้าย</div>
        )}
      </section>

      {/* ข้อมูลผู้รับบริการ */}
      {selectedChat && (
        <aside className="hv4-chat-side" aria-label="ข้อมูลผู้รับบริการ" ref={asideRef}>
          {bookOpen && (
            <section className="hv4-chat-book" aria-label="สร้างนัดหมายในแชท">
              <div className="hv4-chat-book-head">
                <b>สร้างนัดหมายในแชท</b>
                <ButtonV4
                  variant="plain"
                  size="icon"
                  aria-label="ปิดฟอร์ม"
                  title="ปิด"
                  onClick={handleCloseBooking}
                >
                  <X size={18} aria-hidden="true" />
                </ButtonV4>
              </div>
              {!!bookDone && (
                <div role="status" className="hv4-chat-book-done">
                  {bookDone}
                </div>
              )}
              <div ref={bookRef} className="hv4-chat-book-v3">
                <BookingSection
                  appointments={props.selectedPatientAppointmentList as any}
                  isCollapsed={bookCollapsed}
                  officialAccountList={props.officialAccountList}
                  selectedChat={selectedChat}
                  onEvent={props.onEvent}
                  onToggle={handleToggleBooking}
                  timeDivision={props.timeDivision}
                  createAppointment={props.createAppointment}
                  loadingStatus={props.loadingStatus}
                  errorMessage={props.errorMessage}
                  createdAppointment={props.createdAppointment}
                  setProp={props.setProp}
                />
              </div>
            </section>
          )}

          <div className="hv4-chat-who">
            <Avatar name={patientDisplayName(props.selectedPatientChat, selectedChat)} size="lg" />
            <b>{patientDisplayName(props.selectedPatientChat, selectedChat)}</b>
          </div>

          <div className="hv4-chat-tabs" role="group" aria-label="ข้อมูลผู้รับบริการ">
            {CHAT_TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`hv4-chat-tab${tab === item.key ? " hv4-chat-tab--active" : ""}`}
                aria-pressed={tab === item.key}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tabShowsCards(tab) ? (
            <>
              <section className="hv4-chat-card">
                <button
                  type="button"
                  className="hv4-chat-card-head"
                  aria-expanded={openInfo}
                  onClick={handleToggleInfo}
                >
                  ข้อมูลส่วนบุคคล
                  {openInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openInfo && (
                  <dl className="hv4-chat-info">
                    {patientInfoRows(props.selectedPatientChat, props.selectedProxyPatient).map(
                      (row) => (
                        <React.Fragment key={row.label}>
                          <dt>{row.label}</dt>
                          <dd>{row.value}</dd>
                        </React.Fragment>
                      ),
                    )}
                  </dl>
                )}
              </section>

              <section className="hv4-chat-card">
                <button
                  type="button"
                  className="hv4-chat-card-head"
                  aria-expanded={openAppt}
                  onClick={handleToggleAppt}
                >
                  ข้อมูลนัดหมาย
                  {openAppt ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openAppt && (
                  <div className="hv4-chat-appts">
                    {props.loadingStatus?.[APPOINTMENT_LIST_CARD] ? (
                      <div className="hv4-chat-appt-empty">กำลังโหลด…</div>
                    ) : appointments.length === 0 ? (
                      <div className="hv4-chat-appt-empty">ยังไม่มีนัดหมาย</div>
                    ) : (
                      appointments.map((item) => (
                        <div key={item.id} className="hv4-chat-appt">
                          <div className="hv4-chat-appt-top">
                            <b>{item.provider}</b>
                            <ChipV4 tone={item.statusTone} size="sm" bold>
                              {item.statusText}
                            </ChipV4>
                          </div>
                          <div>
                            วันเวลาที่นัดหมาย : {item.date}
                            {item.time ? ` · ${item.time}` : ""}
                          </div>
                        </div>
                      ))
                    )}
                    {!bookOpen && (
                      <ButtonV4
                        variant="primary"
                        className="hv4-chat-book-btn"
                        onClick={handleOpenBooking}
                      >
                        + สร้างนัดหมายให้ลูกค้ารายนี้
                      </ButtonV4>
                    )}
                    {!!props.onGoAppointments && (
                      <button
                        type="button"
                        className="hv4-chat-link"
                        onClick={props.onGoAppointments}
                      >
                        ดูในหน้านัดหมาย →
                      </button>
                    )}
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="hv4-chat-pending">{PENDING_TEXT[tab as "form" | "note"]}</div>
          )}
        </aside>
      )}

      {!!props.modOpenLinkEncounter && (
        <ModalV4 title="แจ้งเตือน" width={480} onClose={handleCloseLinkEncounter}>
          <div
            className="hv4-chat-modal-text"
            dangerouslySetInnerHTML={{ __html: props.modOpenLinkEncounter }}
          />
        </ModalV4>
      )}
    </div>
  );
});

CardChatV4.displayName = "CardChatV4";

export default CardChatV4;
